import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AudioPlayerService } from './audio-player.service';

/**
 * Single source of truth for the softphone ringtone volume.
 *
 * The ringtone audio element lives in AudioPlayerService and is played from
 * ZoidAppService.phoneActions (PLAY_RINGTONE). The volume is persisted in
 * localStorage so it survives reloads and is read back at play time.
 */
@Injectable({
  providedIn: 'root',
})
export class RingtoneVolumeService {
  static readonly RINGTONE_AUDIO = 'ringtone.mp3';
  static readonly STORAGE_KEY = 'ringtone_volume';
  static readonly DEFAULT_VOLUME = 1;

  private audioPlayerService = inject(AudioPlayerService);

  /** Whether the ringtone volume icon should be shown. Driven by phone-app messages. */
  private readonly iconVisibleSub = new BehaviorSubject<boolean>(true);
  readonly iconVisible$ = this.iconVisibleSub.asObservable();

  /** Show/hide the volume icon, in response to WEBPHONE_SHOW/HIDE_VOLUME_ICON messages. */
  setIconVisible(visible: boolean): void {
    this.iconVisibleSub.next(visible);
  }

  /** Persisted ringtone volume, clamped to 0..1. Defaults to 1 when unset/invalid. */
  get volume(): number {
    const stored = localStorage.getItem(RingtoneVolumeService.STORAGE_KEY);
    const parsed = Number(stored);
    return stored !== null && Number.isFinite(parsed)
      ? this.clamp(parsed)
      : RingtoneVolumeService.DEFAULT_VOLUME;
  }

  /** Persist the volume and apply it live to the (possibly already looping) ringtone element. */
  setVolume(value: number): void {
    const volume = this.clamp(Number(value));
    if (!Number.isFinite(volume)) {
      return;
    }
    localStorage.setItem(RingtoneVolumeService.STORAGE_KEY, String(volume));
    this.audioPlayerService.setVolume(RingtoneVolumeService.RINGTONE_AUDIO, volume);
  }

  private clamp(value: number): number {
    return Math.min(1, Math.max(0, value));
  }
}
